/**
 * Frontend Logging Utility
 * Provides structured logging for the Tekstation frontend application
 */

class Logger {
  constructor() {
    this.isDebug = process.env.REACT_APP_DEBUG === 'true';
    this.environment = process.env.REACT_APP_ENV || 'unknown';
  }

  /**
   * Generate a unique correlation ID for tracking user sessions
   */
  generateCorrelationId() {
    return `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current correlation ID from session storage or generate new one
   */
  getCorrelationId() {
    let correlationId = sessionStorage.getItem('correlationId');
    if (!correlationId) {
      correlationId = this.generateCorrelationId();
      sessionStorage.setItem('correlationId', correlationId);
    }
    return correlationId;
  }

  /**
   * Format log message with structured data
   */
  formatMessage(level, event, data = {}) {
    const timestamp = new Date().toISOString();
    const correlationId = this.getCorrelationId();
    
    return {
      timestamp,
      level,
      environment: this.environment,
      correlationId,
      event,
      ...data
    };
  }

  /**
   * Log info message
   */
  info(event, data = {}) {
    const logData = this.formatMessage('INFO', event, data);
    
    if (this.isDebug) {
      console.log(`[INFO] ${event}:`, logData);
    }
    
    // In production, you might send this to a logging service
    this.sendToLoggingService(logData);
  }

  /**
   * Log warning message
   */
  warn(event, data = {}) {
    const logData = this.formatMessage('WARN', event, data);
    
    if (this.isDebug) {
      console.warn(`[WARN] ${event}:`, logData);
    }
    
    this.sendToLoggingService(logData);
  }

  /**
   * Log error message
   */
  error(event, data = {}) {
    const logData = this.formatMessage('ERROR', event, data);
    
    if (this.isDebug) {
      console.error(`[ERROR] ${event}:`, logData);
    }
    
    this.sendToLoggingService(logData);
  }

  /**
   * Log debug message
   */
  debug(event, data = {}) {
    const logData = this.formatMessage('DEBUG', event, data);
    
    if (this.isDebug) {
      console.debug(`[DEBUG] ${event}:`, logData);
    }
  }

  /**
   * Log user interaction
   */
  userInteraction(action, element, data = {}) {
    this.info('USER_INTERACTION', {
      action,
      element,
      ...data,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  /**
   * Log API request
   */
  apiRequest(method, url, data = {}) {
    this.info('API_REQUEST', {
      method,
      url,
      ...data
    });
  }

  /**
   * Log API response
   */
  apiResponse(method, url, status, duration, data = {}) {
    this.info('API_RESPONSE', {
      method,
      url,
      status,
      duration: `${duration}ms`,
      ...data
    });
  }

  /**
   * Log API error
   */
  apiError(method, url, error, data = {}) {
    this.error('API_ERROR', {
      method,
      url,
      error: error.message || error,
      stack: error.stack,
      ...data
    });
  }

  /**
   * Log form submission
   */
  formSubmission(formName, data = {}) {
    this.info('FORM_SUBMISSION', {
      formName,
      ...data
    });
  }

  /**
   * Log form validation error
   */
  formValidationError(formName, errors, data = {}) {
    this.warn('FORM_VALIDATION_ERROR', {
      formName,
      errors,
      ...data
    });
  }

  /**
   * Log performance metrics
   */
  performance(metric, value, data = {}) {
    this.info('PERFORMANCE', {
      metric,
      value,
      ...data
    });
  }

  /**
   * Send log data to logging service (placeholder for production)
   */
  sendToLoggingService(logData) {
    // In production, you might send logs to a service like:
    // - Loggly
    // - Datadog
    // - Splunk
    // - Your own logging endpoint
    
    if (this.environment === 'production') {
      // Example: Send to your logging endpoint
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logData)
      // }).catch(err => {
      //   // Silently fail to avoid infinite logging loops
      // });
    }
  }

  /**
   * Get browser and device information
   */
  getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      onlineStatus: navigator.onLine
    };
  }

  /**
   * Log application lifecycle events
   */
  appLifecycle(event, data = {}) {
    this.info('APP_LIFECYCLE', {
      event,
      deviceInfo: this.getDeviceInfo(),
      ...data
    });
  }
}

// Create and export singleton instance
const logger = new Logger();
export default logger;
